// Enhanced Client Database System
// This replaces the hardcoded companyConfig.js with a more flexible system

// Load data from localStorage or initialize with defaults
const loadFromStorage = (key, defaultData) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultData;
  } catch (error) {
    console.warn(`Error loading ${key} from localStorage:`, error);
    return defaultData;
  }
};

const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn(`Error saving ${key} to localStorage:`, error);
  }
};

// Default companies seeded into every fresh browser
const DEFAULT_COMPANIES = {
  '1209-digital': {
    id: '1209-digital',
    name: '1209 Digital',
    type: 'admin',
    branding: {
      primaryColor: '#1e3a8a',
      secondaryColor: '#3b82f6',
      logo: null,
      customCSS: null
    },
    settings: {
      allowTeamInvites: true,
      maxTeamMembers: -1,
      fileTypes: ['all'],
      storageLimit: -1
    },
    createdDate: '2024-01-01',
    status: 'active',
    powerBIReports: []
  },
  'elixir-shots': {
    id: 'elixir-shots',
    name: 'Elixir Shots',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'elixir-shots',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  },
  'juni': {
    id: 'juni',
    name: 'Juni',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'juni',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  },
  'na-beverage': {
    id: 'na-beverage',
    name: 'NA Beverage',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'na-beverage',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  },
  'no-cap-soda': {
    id: 'no-cap-soda',
    name: 'No Cap Soda',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'no-cap-soda',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  },
  'oku-energy': {
    id: 'oku-energy',
    name: 'Oku',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'oku-energy',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  },
  'proda': {
    id: 'proda',
    name: 'Proda',
    type: 'client',
    branding: { primaryColor: '#1e40af', secondaryColor: '#3b82f6', logo: null, customCSS: null },
    settings: { allowTeamInvites: true, maxTeamMembers: 10, fileTypes: ['pdf', 'doc', 'xls', 'ppt', 'img'], storageLimit: 10737418240 },
    createdDate: '2024-01-01',
    status: 'active',
    s3BrandFolder: 'proda',
    powerBIReports: [],
    portalAccess: { enabled: true, customDomain: null, welcomeMessage: 'Welcome to your secure portal' }
  }
};

// Initialize with persistent data, merging defaults with any stored data
const storedCompanies = loadFromStorage('companies', null);
let companies = storedCompanies
  ? { ...DEFAULT_COMPANIES, ...storedCompanies }
  : { ...DEFAULT_COMPANIES };

let users = loadFromStorage('users', {
  'ryan@1209digital.com': {
    email: 'ryan@1209digital.com',
    name: 'Ryan',
    companyId: '1209-digital',
    role: 'owner',
    joinedDate: '2024-01-01',
    lastLogin: null,
    invitedBy: null,
    status: 'active',
    approvalStatus: 'approved' // New field: 'pending', 'approved', 'rejected'
  }
});

let invitations = loadFromStorage('invitations', {});

// Company Management Functions
export const createCompany = (companyData) => {
  const companyId = companyData.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
  companies[companyId] = {
    id: companyId,
    name: companyData.name,
    type: 'client',
    branding: {
      primaryColor: companyData.primaryColor || '#1e40af',
      secondaryColor: companyData.secondaryColor || '#3b82f6',
      logo: companyData.logo || null,
      customCSS: null
    },
    settings: {
      allowTeamInvites: true,
      maxTeamMembers: companyData.maxTeamMembers || 10,
      fileTypes: companyData.fileTypes || ['pdf', 'doc', 'xls', 'ppt', 'img'],
      storageLimit: companyData.storageLimit || 10737418240 // 10GB default
    },
    createdDate: new Date().toISOString().split('T')[0],
    status: 'active',
    localFolderPath: companyData.localFolderPath || null,
    s3BrandFolder: companyData.s3BrandFolder || null, // AWS S3 brand folder name for keyword data
    powerBIReports: companyData.powerBIReports || [],
    portalAccess: {
      enabled: true,
      customDomain: null,
      welcomeMessage: companyData.welcomeMessage || `Welcome to your secure portal`
    }
  };

  // Save to localStorage
  saveToStorage('companies', companies);

  return companyId;
};

export const getCompany = (companyId) => {
  return companies[companyId] || null;
};

export const getAllCompanies = () => {
  return Object.values(companies).filter(c => c.status === 'active');
};

export const getClientCompanies = () => {
  return Object.values(companies).filter(c => c.type === 'client' && c.status === 'active');
};

export const updateCompanyBranding = (companyId, branding) => {
  if (companies[companyId]) {
    companies[companyId].branding = {
      ...companies[companyId].branding,
      ...branding
    };
    saveToStorage('companies', companies);
    return true;
  }
  return false;
};

export const updateCompanyS3BrandFolder = (companyId, s3BrandFolder) => {
  if (companies[companyId]) {
    companies[companyId].s3BrandFolder = s3BrandFolder;
    saveToStorage('companies', companies);
    return true;
  }
  return false;
};

// User Management Functions
export const createUser = (userData) => {
  users[userData.email] = {
    email: userData.email,
    name: userData.name,
    companyId: userData.companyId,
    role: userData.role || 'viewer',
    joinedDate: new Date().toISOString().split('T')[0],
    lastLogin: null,
    invitedBy: userData.invitedBy || null,
    status: 'active',
    approvalStatus: userData.approvalStatus || 'pending' // Default to pending for new users
  };

  // Save to localStorage
  saveToStorage('users', users);

  return userData.email;
};

export const getUser = (email) => {
  return users[email] || null;
};

export const getUserWithCompany = (email) => {
  const user = users[email];
  if (!user) return null;

  const company = companies[user.companyId];
  return {
    ...user,
    company: company || null
  };
};

export const getCompanyUsers = (companyId) => {
  return Object.values(users).filter(u =>
    u.companyId === companyId && u.status === 'active'
  );
};

export const updateUserRole = (email, newRole) => {
  if (users[email]) {
    users[email].role = newRole;
    saveToStorage('users', users);
    return true;
  }
  return false;
};

export const removeUser = (email) => {
  if (users[email]) {
    users[email].status = 'inactive';
    saveToStorage('users', users);
    return true;
  }
  return false;
};

// User Approval Management
export const approveUser = (email) => {
  if (users[email]) {
    users[email].approvalStatus = 'approved';
    saveToStorage('users', users);
    return true;
  }
  return false;
};

export const rejectUser = (email) => {
  if (users[email]) {
    users[email].approvalStatus = 'rejected';
    users[email].status = 'inactive';
    saveToStorage('users', users);
    return true;
  }
  return false;
};

export const getPendingUsers = () => {
  return Object.values(users).filter(u =>
    u.approvalStatus === 'pending' && u.status === 'active'
  );
};

export const getAllUsers = () => {
  return Object.values(users);
};

// Invitation System
export const createInvitation = (inviterEmail, inviteeEmail, companyId, role = 'viewer') => {
  const inviteId = `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 7); // 7 days expiry

  invitations[inviteId] = {
    id: inviteId,
    inviterEmail,
    inviteeEmail,
    companyId,
    role,
    createdDate: new Date().toISOString(),
    expiryDate: expiryDate.toISOString(),
    status: 'pending'
  };

  saveToStorage('invitations', invitations);
  return inviteId;
};

export const getInvitation = (inviteId) => {
  const invitation = invitations[inviteId];
  if (!invitation) return null;

  // Check if expired
  if (new Date(invitation.expiryDate) < new Date()) {
    invitation.status = 'expired';
  }

  return invitation;
};

export const acceptInvitation = (inviteId, userData) => {
  const invitation = getInvitation(inviteId);
  if (!invitation || invitation.status !== 'pending') {
    return { success: false, error: 'Invalid or expired invitation' };
  }

  // Create the user - invited users are pre-approved
  createUser({
    email: invitation.inviteeEmail,
    name: userData.name,
    companyId: invitation.companyId,
    role: invitation.role,
    invitedBy: invitation.inviterEmail,
    approvalStatus: 'approved' // Invited users don't need approval
  });

  // Mark invitation as accepted
  invitation.status = 'accepted';
  invitation.acceptedDate = new Date().toISOString();

  saveToStorage('invitations', invitations);
  return { success: true, companyId: invitation.companyId };
};

export const getPendingInvitations = (companyId) => {
  return Object.values(invitations).filter(inv =>
    inv.companyId === companyId &&
    inv.status === 'pending' &&
    new Date(inv.expiryDate) > new Date()
  );
};

// Authorization Functions
export const canInviteUsers = (email) => {
  const user = users[email];
  if (!user) return false;

  const company = companies[user.companyId];
  if (!company || !company.settings.allowTeamInvites) return false;

  return user.role === 'owner' || user.role === 'admin';
};

export const canManageCompany = (email, companyId) => {
  const user = users[email];
  if (!user) return false;

  // System admins can manage any company
  if (user.companyId === '1209-digital' && user.role === 'owner') {
    return true;
  }

  // Company admins can manage their own company
  return user.companyId === companyId &&
         (user.role === 'owner' || user.role === 'admin');
};

export const isSystemAdmin = (email) => {
  const user = users[email];
  return user && user.companyId === '1209-digital' &&
         (user.role === 'owner' || user.role === 'admin');
};

// Folder Sync Configuration
export const setCompanyLocalFolder = (companyId, folderPath) => {
  if (companies[companyId]) {
    companies[companyId].localFolderPath = folderPath;
    return true;
  }
  return false;
};

export const getCompanyLocalFolder = (companyId) => {
  const company = companies[companyId];
  return company ? company.localFolderPath : null;
};

// Power BI Report Management
export const addPowerBIReport = (companyId, reportData) => {
  if (companies[companyId]) {
    const report = {
      id: reportData.id || `report_${Date.now()}`,
      title: reportData.title,
      embedUrl: reportData.embedUrl,
      description: reportData.description || '',
      category: reportData.category || 'General',
      isActive: reportData.isActive !== false,
      addedDate: new Date().toISOString()
    };

    if (!companies[companyId].powerBIReports) {
      companies[companyId].powerBIReports = [];
    }

    companies[companyId].powerBIReports.push(report);
    saveToStorage('companies', companies);
    return report.id;
  }
  return null;
};

export const updatePowerBIReport = (companyId, reportId, reportData) => {
  if (companies[companyId] && companies[companyId].powerBIReports) {
    const reportIndex = companies[companyId].powerBIReports.findIndex(r => r.id === reportId);
    if (reportIndex !== -1) {
      companies[companyId].powerBIReports[reportIndex] = {
        ...companies[companyId].powerBIReports[reportIndex],
        ...reportData,
        updatedDate: new Date().toISOString()
      };
      saveToStorage('companies', companies);
      return true;
    }
  }
  return false;
};

export const removePowerBIReport = (companyId, reportId) => {
  if (companies[companyId] && companies[companyId].powerBIReports) {
    companies[companyId].powerBIReports = companies[companyId].powerBIReports.filter(r => r.id !== reportId);
    saveToStorage('companies', companies);
    return true;
  }
  return false;
};

export const getCompanyPowerBIReports = (companyId) => {
  const company = companies[companyId];
  return company && company.powerBIReports ? company.powerBIReports.filter(r => r.isActive) : [];
};

// Persist merged defaults back to localStorage so new clients are saved
saveToStorage('companies', companies);

// ============================================
// SHARED RESOURCES MANAGEMENT
// ============================================

// Add shared resource (Google Drive, Dropbox, etc.) to a company
export const addSharedResource = (companyId, resourceData) => {
  const company = companies[companyId];
  if (!company) {
    return { success: false, error: 'Company not found' };
  }

  // Initialize sharedResources array if it doesn't exist
  if (!company.sharedResources) {
    company.sharedResources = [];
  }

  const newResource = {
    id: Date.now().toString(),
    name: resourceData.name || 'Untitled Resource',
    url: resourceData.url,
    type: resourceData.type || 'other', // google-drive, dropbox, onedrive, other
    description: resourceData.description || '',
    addedDate: new Date().toISOString(),
    addedBy: resourceData.addedBy || null
  };

  company.sharedResources.push(newResource);
  saveToStorage('companies', companies);

  return { success: true, resource: newResource };
};

// Get all shared resources for a company
export const getSharedResources = (companyId) => {
  const company = companies[companyId];
  if (!company) {
    return [];
  }

  return company.sharedResources || [];
};

// Update a shared resource
export const updateSharedResource = (companyId, resourceId, updates) => {
  const company = companies[companyId];
  if (!company || !company.sharedResources) {
    return { success: false, error: 'Company or resources not found' };
  }

  const resourceIndex = company.sharedResources.findIndex(r => r.id === resourceId);
  if (resourceIndex === -1) {
    return { success: false, error: 'Resource not found' };
  }

  company.sharedResources[resourceIndex] = {
    ...company.sharedResources[resourceIndex],
    ...updates,
    id: resourceId // Ensure ID doesn't change
  };

  saveToStorage('companies', companies);

  return { success: true, resource: company.sharedResources[resourceIndex] };
};

// Remove a shared resource
export const removeSharedResource = (companyId, resourceId) => {
  const company = companies[companyId];
  if (!company || !company.sharedResources) {
    return { success: false, error: 'Company or resources not found' };
  }

  const resourceIndex = company.sharedResources.findIndex(r => r.id === resourceId);
  if (resourceIndex === -1) {
    return { success: false, error: 'Resource not found' };
  }

  company.sharedResources.splice(resourceIndex, 1);
  saveToStorage('companies', companies);

  return { success: true };
};

// Export all functions for use in components
export default {
  // Companies
  createCompany,
  getCompany,
  getAllCompanies,
  getClientCompanies,
  updateCompanyBranding,
  updateCompanyS3BrandFolder,
  setCompanyLocalFolder,
  getCompanyLocalFolder,

  // Users
  createUser,
  getUser,
  getUserWithCompany,
  getCompanyUsers,
  updateUserRole,
  removeUser,
  approveUser,
  rejectUser,
  getPendingUsers,
  getAllUsers,

  // Invitations
  createInvitation,
  getInvitation,
  acceptInvitation,
  getPendingInvitations,

  // Power BI Reports
  addPowerBIReport,
  updatePowerBIReport,
  removePowerBIReport,
  getCompanyPowerBIReports,

  // Shared Resources
  addSharedResource,
  getSharedResources,
  updateSharedResource,
  removeSharedResource,

  // Authorization
  canInviteUsers,
  canManageCompany,
  isSystemAdmin
};